import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { getAdsForPosition, type AdSlotPosition } from "@/stores/adSlotsStore";
import { useAdSlotsStore } from "@/stores/adSlotsStore";
import { useAdsStore, logAdEvent } from "@/stores/adsStore";
import { resolveAdForSlot, type ResolvedAd } from "@/lib/adResolver";
import { useTenantStore } from "@/lib/tenantContext";
import { ExternalLink } from "lucide-react";
import { transformSupabaseImage } from "@/lib/imageTransform";
import { useAdOptoutStore } from "@/stores/adOptoutStore";

interface AdSlotRendererProps {
  position: AdSlotPosition;
  /** Optional page key override. Defaults derived from current pathname. */
  page?: string;
  /** When true, the first image ad is eagerly loaded with high fetch priority. */
  priority?: boolean;
}

function derivePage(pathname: string): string {
  if (pathname === "/" || pathname === "" || pathname.startsWith("/index")) return "home";
  if (pathname.startsWith("/track")) return "tracking";
  if (pathname.startsWith("/swap")) return "swap";
  if (pathname.startsWith("/review")) return "checkout";
  if (pathname.startsWith("/account")) return "home";
  return "home";
}

const AdSlotRenderer = ({ position, page, priority = false }: AdSlotRendererProps) => {
  const location = useLocation();
  const pageKey = page ?? derivePage(location.pathname);
  const tenantId = useTenantStore(s => s.tenantId);

  // Ad-free opt-out check (bypasses all ad rendering for opted-out tenants).
  const [optedOut, setOptedOut] = useState(false);
  useEffect(() => {
    if (!tenantId) { setOptedOut(false); return; }
    useAdOptoutStore.getState().isActive(tenantId).then(setOptedOut);
  }, [tenantId]);

  // Boot ads store once.
  const adsLoaded = useAdsStore(s => s.loaded);
  const adsLoading = useAdsStore(s => s.loading);
  useEffect(() => {
    if (!adsLoaded && !adsLoading) { useAdsStore.getState().load(); }
  }, [adsLoaded, adsLoading]);

  const campaignsRev = useAdsStore(s => s.campaigns.length + ":" + s.creatives.length + ":" + (s.loaded ? 1 : 0));
  const adsRev = useAdSlotsStore(s => s._rev);

  // Resolver-driven ad (priority over legacy positional slots).
  const resolved: ResolvedAd | null = useMemo(
    () => resolveAdForSlot({ tenantId, page: pageKey, position }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tenantId, pageKey, position, campaignsRev]
  );

  const legacyAds = useMemo(() => (resolved ? [] : getAdsForPosition(position)), [position, adsRev, resolved]);

  // Impression log: once per (campaign, position) mount.
  const impressionKey = resolved ? `${resolved.campaign.id}:${pageKey}:${position}` : null;
  const loggedRef = useRef<string | null>(null);
  useEffect(() => {
    if (impressionKey && loggedRef.current !== impressionKey) {
      loggedRef.current = impressionKey;
      logAdEvent({
        campaign_id: resolved!.campaign.id,
        tenant_id: tenantId,
        event_type: "impression",
        page: pageKey,
        position,
        ab_variant_group: resolved!.campaign.ab_variant_group,
      });
    }
  }, [impressionKey, resolved, tenantId, pageKey, position]);

  if (optedOut) return null;

  if (resolved) {
    const onClick = () => {
      logAdEvent({
        campaign_id: resolved.campaign.id,
        tenant_id: tenantId,
        event_type: "click",
        page: pageKey,
        position,
        ab_variant_group: resolved.campaign.ab_variant_group,
      });
    };
    return (
      <div className="space-y-2">
        <a
          href={resolved.link_url || "#"}
          target={resolved.link_url ? "_blank" : undefined}
          rel="noopener noreferrer sponsored"
          onClick={onClick}
          className="block relative group overflow-hidden rounded-lg border border-border/50 transition-all hover:shadow-card"
        >
          <img
            src={transformSupabaseImage(resolved.image_url, { width: 1200, quality: 75 })}
            alt={resolved.label || resolved.campaign.name}
            className="w-full h-auto max-h-28 object-contain bg-muted"
            loading={priority ? "eager" : "lazy"}
            {...(priority ? { fetchpriority: "high" as any, decoding: "async" as const } : { decoding: "async" as const })}
          />
          <span className="absolute top-1 right-1 text-[8px] bg-background/80 text-muted-foreground px-1.5 py-0.5 rounded opacity-70">
            Ad
          </span>
        </a>
      </div>
    );
  }

  if (legacyAds.length === 0) return null;

  return (
    <div className="space-y-2">
      {legacyAds.map((ad, idx) => {
        const isLcpCandidate = priority && idx === 0;
        return (
          <a
            key={ad.id}
            href={ad.linkUrl || "#"}
            target={ad.linkUrl ? "_blank" : undefined}
            rel="noopener noreferrer sponsored"
            className="block relative group overflow-hidden rounded-lg border border-border/50 transition-all hover:shadow-card"
          >
            {ad.imageUrl ? (
              <img
                src={transformSupabaseImage(ad.imageUrl, { width: 1200, quality: 75 })}
                alt={ad.label || "Sponsored"}
                className="w-full h-auto max-h-28 object-contain bg-muted"
                loading={isLcpCandidate ? "eager" : "lazy"}
                {...(isLcpCandidate ? { fetchpriority: "high" as any, decoding: "async" as const } : { decoding: "async" as const })}
              />
            ) : (
              <div className="bg-accent/30 px-4 py-3 text-center">
                <p className="text-sm font-medium text-foreground">{ad.label || "Sponsored"}</p>
                {ad.linkUrl && (
                  <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground mt-1">
                    <ExternalLink className="w-3 h-3" /> Learn more
                  </div>
                )}
              </div>
            )}
            <span className="absolute top-1 right-1 text-[8px] bg-background/80 text-muted-foreground px-1.5 py-0.5 rounded opacity-70">
              Ad
            </span>
          </a>
        );
      })}
    </div>
  );
};

export default AdSlotRenderer;
